import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BOX_SHADOW_THEME,
  GRAY_600,
  INPUT_COLOR_BORDER,
} from "../../../utils/colors";
import { BORDER_RADIUS } from "../../../utils/constants";
import {
  AI_COMPETITORS_COMPETITORS_ADD,
  AI_COMPETITORS_COMPETITORS_ADD_INVALID,
  AI_COMPETITORS_COMPETITORS_ADD_PLACEHOLDER,
  AI_COMPETITORS_COMPETITORS_CTA,
  AI_COMPETITORS_COMPETITORS_DESCRIPTION,
  AI_COMPETITORS_COMPETITORS_EMPTY,
  AI_COMPETITORS_COMPETITORS_MUTED,
  AI_COMPETITORS_COMPETITORS_NAME_LABEL,
  AI_COMPETITORS_COMPETITORS_NEED_ONE,
  AI_COMPETITORS_COMPETITORS_REMOVE,
  AI_COMPETITORS_COMPETITORS_STEP_NUMBER,
  AI_COMPETITORS_COMPETITORS_TITLE,
} from "../../../i18n/keysTranslations";
import {
  AI_COMPETITORS_MAX_COMPETITORS,
  AI_COMPETITORS_OUTLINED_INPUT_SX,
  AI_COMPETITORS_PRIMARY_BUTTON_SX,
} from "../aiCompetitorsConstants";
import {
  domainToBrandName,
  extractDomain,
  looksLikeDomain,
} from "../aiCompetitorsUrl";
import AiCompetitorsStepIntro from "./AiCompetitorsStepIntro";

const ADD_DOMAIN_ROW_HEIGHT = 45;

const createCompetitorId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `comp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const AiCompetitorsCompetitorsStep = ({
  competitors = [],
  onChangeCompetitors,
  onSearchAds,
}) => {
  const { t } = useTranslationApp();
  const [domainInput, setDomainInput] = useState("");
  const [addError, setAddError] = useState("");
  const [needOneError, setNeedOneError] = useState(false);

  const onRemove = (competitorId) => {
    onChangeCompetitors(
      competitors.filter((competitor) => competitor.id !== competitorId)
    );
  };

  const onRename = (competitorId, name) => {
    onChangeCompetitors(
      competitors.map((competitor) =>
        competitor.id === competitorId ? { ...competitor, name } : competitor
      )
    );
  };

  const atMaxCompetitors = competitors.length >= AI_COMPETITORS_MAX_COMPETITORS;

  const onAdd = () => {
    if (atMaxCompetitors) {
      return;
    }
    const domain = extractDomain(domainInput);
    if (!looksLikeDomain(domainInput) || !domain) {
      setAddError(AI_COMPETITORS_COMPETITORS_ADD_INVALID);
      return;
    }
    if (competitors.some((competitor) => competitor.domain === domain)) {
      setDomainInput("");
      setAddError("");
      return;
    }
    onChangeCompetitors([
      ...competitors,
      {
        id: createCompetitorId(),
        name: domainToBrandName(domain),
        domain,
        source: "user",
      },
    ]);
    setDomainInput("");
    setAddError("");
    setNeedOneError(false);
  };

  const onContinue = () => {
    if (competitors.length === 0) {
      setNeedOneError(true);
      return;
    }
    onSearchAds();
  };

  return (
    <Box>
      <AiCompetitorsStepIntro
        stepNumber={t(AI_COMPETITORS_COMPETITORS_STEP_NUMBER)}
        title={t(AI_COMPETITORS_COMPETITORS_TITLE)}
        mutedTitle={t(AI_COMPETITORS_COMPETITORS_MUTED)}
        description={t(AI_COMPETITORS_COMPETITORS_DESCRIPTION)}
      />
      {competitors.length === 0 ? (
        <Typography
          sx={{ mt: 2, fontSize: 14, color: "text.secondary", lineHeight: 1.6 }}
        >
          {t(AI_COMPETITORS_COMPETITORS_EMPTY)}
        </Typography>
      ) : null}
      <Box
        sx={{
          mt: { xs: 3, sm: 4 },
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {competitors.map((competitor) => (
          <Paper
            key={competitor.id}
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: { xs: 1.5, sm: 2 },
              borderRadius: BORDER_RADIUS,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: BOX_SHADOW_THEME,
              backgroundColor: "fields.background",
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <TextField
                value={competitor.name}
                onChange={(event) =>
                  onRename(competitor.id, event.target.value)
                }
                label={t(AI_COMPETITORS_COMPETITORS_NAME_LABEL)}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <EditOutlinedIcon
                        sx={{ fontSize: 16, color: GRAY_600 }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={AI_COMPETITORS_OUTLINED_INPUT_SX}
              />
              <Typography
                sx={{
                  mt: 0.75,
                  fontFamily: "monospace",
                  fontSize: 13,
                  color: GRAY_600,
                  wordBreak: "break-all",
                }}
              >
                {competitor.domain}
              </Typography>
            </Box>
            <IconButton
              aria-label={t(AI_COMPETITORS_COMPETITORS_REMOVE)}
              onClick={() => onRemove(competitor.id)}
              sx={{ mt: 0.25 }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Paper>
        ))}
      </Box>
      {atMaxCompetitors ? null : (
        <Box
          sx={{
            mt: { xs: 3.5, sm: 4.5 },
            display: "flex",
            gap: 1,
            alignItems: "stretch",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <TextField
            value={domainInput}
            onChange={(event) => {
              setDomainInput(event.target.value);
              if (addError) setAddError("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onAdd();
              }
            }}
            placeholder={t(AI_COMPETITORS_COMPETITORS_ADD_PLACEHOLDER)}
            fullWidth
            error={Boolean(addError)}
            sx={[
              AI_COMPETITORS_OUTLINED_INPUT_SX,
              {
                "& .MuiOutlinedInput-root": {
                  height: ADD_DOMAIN_ROW_HEIGHT,
                  boxSizing: "border-box",
                },
              },
            ]}
          />
          <Button
            variant="outlined"
            onClick={onAdd}
            sx={{
              height: ADD_DOMAIN_ROW_HEIGHT,
              minHeight: ADD_DOMAIN_ROW_HEIGHT,
              py: 0,
              minWidth: { xs: "100%", sm: 96 },
              borderRadius: "9px !important",
              textTransform: "none",
              flexShrink: 0,
            }}
          >
            {t(AI_COMPETITORS_COMPETITORS_ADD)}
          </Button>
        </Box>
      )}
      {addError ? (
        <Typography sx={{ mt: 1, fontSize: 12, color: "error.main" }}>
          {t(addError)}
        </Typography>
      ) : null}
      {needOneError ? (
        <Typography sx={{ mt: 1, fontSize: 12, color: "error.main" }}>
          {t(AI_COMPETITORS_COMPETITORS_NEED_ONE)}
        </Typography>
      ) : null}
      <Button
        variant="contained"
        color="primary"
        onClick={onContinue}
        sx={{ ...AI_COMPETITORS_PRIMARY_BUTTON_SX, mt: 3 }}
      >
        {t(AI_COMPETITORS_COMPETITORS_CTA)}
      </Button>
    </Box>
  );
};

export default AiCompetitorsCompetitorsStep;
